import { useCallback, useEffect, useMemo, useState } from 'react';
import { logger } from './lib/logger';
import { Card } from './components/ui/Card';
import { Button } from './components/ui/Button';
import { FormField } from './components/ui/FormField';
import { Input } from './components/ui/Input';
import { Select } from './components/ui/Select';
import type { z } from 'zod';
import {
  FixtureEnvConfigSchema,
  FixtureExecuteResponseSchema,
  FixturePreviewResponseSchema,
  type EnvConfig,
  type FixtureExecuteResponse,
  type FixturePreviewResponse,
  type FixtureType,
} from '@shared/schemas/admin-fixtures';
import { AdminFixtureOpportunityResponseSchema, AdminFixtureTeamResponseSchema } from '@shared/schemas/admin-ops';
import { useApi } from './lib/api';
import { ApiProblemError, fetchJson } from './lib/apiFetchJson';
import clsx from 'clsx';

// Types

type FixturePreviewItem = FixturePreviewResponse['fixtures'][number];
type TeamMember = z.infer<typeof AdminFixtureTeamResponseSchema>['team'][number];

// Constants

const FIXTURE_TYPE_OPTIONS: Array<{ value: FixtureType; label: string; description: string }> = [
  { value: 'wizard', label: 'Stage Update Wizard', description: 'Multi-step approval with AI-suggested stage progression' },
  { value: 'single_field', label: 'Single Field Update', description: 'One-click approval for a single Salesforce field' },
  { value: 'bundle', label: 'Field Bundle', description: 'Multiple field suggestions for the same opportunity' },
  { value: 'insight', label: 'Insight Card', description: 'Read-only informational card (no approval)' },
  { value: 'webhook_post', label: 'Webhook Approval', description: 'Approval that triggers an external webhook' },
  { value: 'binary_approval', label: 'Binary Approval', description: 'Simple approve/reject using Terra metadata' },
];

const BUNDLE_FIELDS = [
  { apiName: 'NextStep', label: 'Next Step' },
  { apiName: 'CloseDate', label: 'Close Date' },
  { apiName: 'Amount', label: 'Amount' },
  { apiName: 'Probability', label: 'Probability' },
  { apiName: 'Description', label: 'Description' },
];

// Test opportunities created by blake.johnson@clay.com in production
const TEST_OPPORTUNITIES = [
  { id: '006a700000Md2TXAAZ', name: 'Blake-Sample', account: 'ACME (Sample)' },
  { id: '006a700000QlxvJAAR', name: 'blake calendar project', account: 'Test Account' },
  { id: '006a700000LtpU1AAJ', name: 'blake-new-stages', account: 'ACME (Sample)' },
  { id: '006a700000JuIlhAAF', name: 'blake-test-sl', account: 'ACME (Sample)' },
  { id: '006a700000ILdAUAA1', name: 'blake-test', account: 'ACME (Sample)' },
];

function isTimeout(e: unknown): boolean {
  return e instanceof ApiProblemError && e.kind === 'aborted';
}

function errorText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

// Component

export default function AdminTestFixtures() {
  const { apiFetch } = useApi();

  // Form state
  const [env, setEnv] = useState('staging');
  const [fixtureType, setFixtureType] = useState<FixtureType>('wizard');
  const [count, setCount] = useState(1);
  const [slackIds, setSlackIds] = useState<string[]>([]);

  // Opportunity selection
  const [selectedOpportunity, setSelectedOpportunity] = useState(TEST_OPPORTUNITIES[0]);
  const [opportunityDetails, setOpportunityDetails] = useState<{
    stage: string | null;
    amount: number | null;
    closeDate: string | null;
    nextStep: string | null;
    probability: number | null;
    description: string | null;
  } | null>(null);
  const [opportunityLoading, setOpportunityLoading] = useState(false);
  const [opportunityError, setOpportunityError] = useState<string | null>(null);

  // Bundle-specific state
  const [selectedFields, setSelectedFields] = useState<string[]>(['NextStep', 'Amount']);

  // Single field state
  const [singleField, setSingleField] = useState(BUNDLE_FIELDS[0].apiName);

  // Environment config (defaults from server)
  const [envConfig, setEnvConfig] = useState<EnvConfig | null>(null);
  // Session overrides for URLs (empty string = use default)
  const [ingestionUrlOverride, setIngestionUrlOverride] = useState('');

  // Team roster (fetched from API)
  const [teamRoster, setTeamRoster] = useState<TeamMember[]>([]);
  const [rosterLoading, setRosterLoading] = useState(true);

  // Results state
  const [preview, setPreview] = useState<FixturePreviewResponse | null>(null);
  const [result, setResult] = useState<FixtureExecuteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showRawJson, setShowRawJson] = useState(false);

  const loadEnvConfig = useCallback(async (environment: string) => {
    try {
      setEnvConfig(await fetchJson(apiFetch, FixtureEnvConfigSchema, `/api/admin/test-fixtures/env-config?env=${environment}`));
    } catch {
      setEnvConfig(null);
    }
  }, [apiFetch]);

  const loadTeamRoster = useCallback(async () => {
    setRosterLoading(true);
    try {
      const data = await fetchJson(apiFetch, AdminFixtureTeamResponseSchema, '/api/admin/test-fixtures/team');
      setTeamRoster(data.team);
    } catch (e) {
      logger.warn({ err: e }, 'Failed to load team roster');
      setTeamRoster([]);
    } finally {
      setRosterLoading(false);
    }
  }, [apiFetch]);

  // Load env config and team roster when env changes
  useEffect(() => {
    void loadEnvConfig(env);
    void loadTeamRoster();
    // Clear selections and URL overrides when env changes
    setSlackIds([]);
    setIngestionUrlOverride('');
  }, [env, loadEnvConfig, loadTeamRoster]);

  async function fetchOpportunityDetails(opportunityId: string) {
    setOpportunityLoading(true);
    setOpportunityError(null);
    setOpportunityDetails(null);
    try {
      const data = await fetchJson(
        apiFetch,
        AdminFixtureOpportunityResponseSchema,
        `/api/admin/test-fixtures/opportunity/${opportunityId}`,
      );
      setOpportunityDetails({
        stage: data.stage,
        amount: data.amount,
        closeDate: data.closeDate,
        nextStep: data.nextStep,
        probability: data.probability,
        description: data.description,
      });
    } catch (e: unknown) {
      logger.warn({ err: e }, 'Failed to fetch opportunity details');
      setOpportunityError(errorText(e, 'Failed to fetch opportunity details'));
    } finally {
      setOpportunityLoading(false);
    }
  }

  function toggleSlackId(slackId: string) {
    setSlackIds((prev) =>
      prev.includes(slackId) ? prev.filter((id) => id !== slackId) : [...prev, slackId]
    );
  }

  function toggleField(apiName: string) {
    setSelectedFields((prev) =>
      prev.includes(apiName) ? prev.filter((f) => f !== apiName) : [...prev, apiName]
    );
  }

  // Build request payload
  const requestPayload = useMemo(() => {
    return {
      env,
      fixtureType,
      slackIds,
      count,
      fieldApiNames: fixtureType === 'bundle' ? selectedFields : undefined,
      fieldApiName: fixtureType === 'single_field' ? singleField : undefined,
      opportunityId: selectedOpportunity.id,
      opportunityName: selectedOpportunity.name,
      accountName: selectedOpportunity.account,
      // URL overrides (only included if different from defaults)
      ingestionUrlOverride: ingestionUrlOverride || undefined,
    };
  }, [env, fixtureType, slackIds, count, selectedFields, singleField, selectedOpportunity, ingestionUrlOverride]);

  async function onPreview() {
    if (slackIds.length === 0) {
      alert('Please add at least one Slack ID');
      return;
    }
    setLoading(true);
    setLoadingMessage('Generating fixtures...');
    setResult(null);
    setPreview(null);
    try {
      // 60s timeout: AI generation can be slow
      const data = await fetchJson(apiFetch, FixturePreviewResponseSchema, '/api/admin/test-fixtures/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...requestPayload, execute: false }),
        timeoutMs: 60_000,
      });
      setPreview(data);
    } catch (e: unknown) {
      logger.error({ err: errorText(e, 'Unknown error') }, 'Preview failed');
      const message = isTimeout(e) ? 'Request timed out (AI generation can be slow)' : errorText(e, 'Unknown error');
      alert(`Preview failed: ${message}`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }

  async function onExecute() {
    if (slackIds.length === 0) {
      alert('Please add at least one Slack ID');
      return;
    }
    if (!preview?.valid) {
      alert('Please fix validation errors before executing');
      return;
    }
    if (env === 'production' && !confirm('Are you sure you want to execute against PRODUCTION?')) {
      return;
    }
    setLoading(true);
    setLoadingMessage('Sending fixtures to ingestion endpoint...');
    try {
      // 60s timeout: AI generation can be slow
      const data = await fetchJson(apiFetch, FixtureExecuteResponseSchema, '/api/admin/test-fixtures/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...requestPayload, execute: true }),
        timeoutMs: 60_000,
      });
      setResult(data);
    } catch (e: unknown) {
      logger.error({ err: errorText(e, 'Unknown error') }, 'Execute failed');
      const message = isTimeout(e) ? 'Request timed out' : errorText(e, 'Unknown error');
      alert(`Execute failed: ${message}`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-bold mb-6">Test Fixtures Generator</h2>

      {/* Environment & Count */}
      <Card className="mb-4">
        <div className="flex flex-wrap gap-4 items-end">
          <FormField label="Environment">
            <Select className="w-32" value={env} onChange={(e) => setEnv(e.target.value)}>
              <option value="local">Local</option>
              <option value="staging">Staging</option>
              <option value="production">Production</option>
            </Select>
          </FormField>
          <FormField label="Count per recipient">
            <Input
              className="w-20"
              type="number"
              value={count}
              min={1}
              max={10}
              onChange={(e) => setCount(Number(e.target.value) || 1)}
            />
          </FormField>
        </div>

        {env === 'production' && (
          <div className="mt-3 rounded-md border border-yellow-400 bg-yellow-50 text-yellow-900 px-3 py-2 text-sm">
            <strong>Warning:</strong> Production environment selected. Use with caution.
          </div>
        )}

        {/* Environment Config - Editable */}
        {envConfig && (
          <div className="mt-3 p-3 bg-gray-50 rounded-md text-sm">
            <div className="text-xs text-gray-500 mb-2">Environment Configuration</div>
            <div className="space-y-3">
              <FormField label="Ingestion URL">
                <div className="flex gap-2 items-center">
                  <Select
                    className="flex-1 font-mono text-xs"
                    value={
                      envConfig.ingestionUrlOptions.some((o) => o.value === (ingestionUrlOverride || envConfig.ingestionUrl))
                        ? ingestionUrlOverride || envConfig.ingestionUrl
                        : '__custom__'
                    }
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        // Keep current value but mark as custom
                        setIngestionUrlOverride(ingestionUrlOverride || envConfig.ingestionUrl);
                      } else {
                        setIngestionUrlOverride(e.target.value === envConfig.ingestionUrl ? '' : e.target.value);
                      }
                    }}
                  >
                    {envConfig.ingestionUrlOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}: {opt.value}
                      </option>
                    ))}
                    <option value="__custom__">Custom...</option>
                  </Select>
                  {envConfig.terraTokenConfigured ? (
                    <span className="text-green-600 text-xs whitespace-nowrap">✓ Token configured</span>
                  ) : (
                    <span className="text-red-600 text-xs whitespace-nowrap">✗ Token missing</span>
                  )}
                </div>
                {/* Show custom input if "Custom" is selected or value doesn't match any option */}
                {!envConfig.ingestionUrlOptions.some((o) => o.value === (ingestionUrlOverride || envConfig.ingestionUrl)) && (
                  <Input
                    className="w-full font-mono text-xs mt-1"
                    value={ingestionUrlOverride || envConfig.ingestionUrl}
                    onChange={(e) => setIngestionUrlOverride(e.target.value)}
                    placeholder="https://..."
                  />
                )}
              </FormField>
            </div>
          </div>
        )}
      </Card>

      {/* Fixture Type Selection */}
      <Card className="mb-4">
        <h3 className="font-semibold mb-3">Fixture Type</h3>
        <div className="space-y-2">
          {FIXTURE_TYPE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={clsx(
                'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                fixtureType === opt.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <input
                type="radio"
                name="fixtureType"
                value={opt.value}
                checked={fixtureType === opt.value}
                onChange={() => setFixtureType(opt.value)}
                className="mt-1"
              />
              <div>
                <div className="font-medium">{opt.label}</div>
                <div className="text-sm text-gray-600">{opt.description}</div>
              </div>
            </label>
          ))}
        </div>

        {/* Single field selection */}
        {fixtureType === 'single_field' && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-2">Select Field to Update</h4>
            <div className="flex flex-wrap gap-2">
              {BUNDLE_FIELDS.map((field) => (
                <button
                  type="button"
                  key={field.apiName}
                  onClick={() => setSingleField(field.apiName)}
                  className={clsx(
                    'inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors text-sm',
                    singleField === field.apiName
                      ? 'border-blue-500 bg-blue-100 text-blue-800'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  )}
                >
                  {field.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bundle field selection */}
        {fixtureType === 'bundle' && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-2">Select Fields to Include</h4>
            <div className="flex flex-wrap gap-2">
              {BUNDLE_FIELDS.map((field) => (
                <button
                  type="button"
                  key={field.apiName}
                  onClick={() => toggleField(field.apiName)}
                  className={clsx(
                    'inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors text-sm',
                    selectedFields.includes(field.apiName)
                      ? 'border-blue-500 bg-blue-100 text-blue-800'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  )}
                >
                  {field.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Salesforce Context */}
      <Card className="mb-4">
        <h3 className="font-semibold mb-3">Salesforce Context</h3>
        <div className="flex gap-2 items-end">
          <FormField label="Test Opportunity" className="flex-1">
            <Select
              className="w-full"
              value={selectedOpportunity.id}
              onChange={(e) => {
                const opp = TEST_OPPORTUNITIES.find((o) => o.id === e.target.value);
                if (opp) {
                  setSelectedOpportunity(opp);
                  setOpportunityDetails(null);
                  setOpportunityError(null);
                }
              }}
            >
              {TEST_OPPORTUNITIES.map((opp) => (
                <option key={opp.id} value={opp.id}>
                  {opp.name} ({opp.account})
                </option>
              ))}
            </Select>
          </FormField>
          <Button
            onClick={() => { void fetchOpportunityDetails(selectedOpportunity.id); }}
            loading={opportunityLoading}
            variant="neutral"
            className="whitespace-nowrap"
          >
            Fetch Details
          </Button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Opportunity:</span>
            <div className="font-medium">{selectedOpportunity.name}</div>
          </div>
          <div>
            <span className="text-gray-500">Account:</span>
            <div className="font-medium">{selectedOpportunity.account}</div>
          </div>
          <div className="col-span-2">
            <span className="text-gray-500">Opportunity ID:</span>
            <code className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded">{selectedOpportunity.id}</code>
          </div>
        </div>

        {/* Opportunity Details from Salesforce */}
        {opportunityError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
            {opportunityError}
          </div>
        )}
        {opportunityDetails && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="text-xs text-blue-600 mb-2 font-medium">Live Salesforce Data</div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <div>
                <span className="text-gray-500">Stage:</span>
                <div className="font-medium text-blue-800">{opportunityDetails.stage || '—'}</div>
              </div>
              <div>
                <span className="text-gray-500">Amount:</span>
                <div className="font-medium">
                  {opportunityDetails.amount != null
                    ? `$${opportunityDetails.amount.toLocaleString()}`
                    : '—'}
                </div>
              </div>
              <div>
                <span className="text-gray-500">Close Date:</span>
                <div className="font-medium">{opportunityDetails.closeDate || '—'}</div>
              </div>
              <div>
                <span className="text-gray-500">Next Step:</span>
                <div className="font-medium truncate" title={opportunityDetails.nextStep || undefined}>
                  {opportunityDetails.nextStep || '—'}
                </div>
              </div>
              <div>
                <span className="text-gray-500">Probability:</span>
                <div className="font-medium">
                  {opportunityDetails.probability != null
                    ? `${opportunityDetails.probability}%`
                    : '—'}
                </div>
              </div>
              <div>
                <span className="text-gray-500">Description:</span>
                <div className="font-medium truncate" title={opportunityDetails.description || undefined}>
                  {opportunityDetails.description || '—'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Recipients */}
      <Card className="mb-4">
        <h3 className="font-semibold mb-3">Recipients</h3>
        <div className="space-y-3">
          {rosterLoading ? (
            <div className="text-sm text-gray-500">Loading team roster...</div>
          ) : (
            <>
              {/* Team roster checkboxes */}
              <div className="flex flex-wrap gap-2">
                {teamRoster.map((member) => {
                  const isSelectable = member.found && member.slackId;
                  const isSelected = member.slackId ? slackIds.includes(member.slackId) : false;

                  return (
                    <button
                      type="button"
                      key={member.email}
                      onClick={() => member.slackId && toggleSlackId(member.slackId)}
                      disabled={!isSelectable}
                      className={clsx(
                        'inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors text-sm',
                        !isSelectable
                          ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                          : isSelected
                            ? 'border-blue-500 bg-blue-100 text-blue-800 cursor-pointer'
                            : 'border-gray-300 bg-white hover:border-gray-400 cursor-pointer'
                      )}
                    >
                      <span className="font-medium">{member.label}</span>
                      {!isSelectable && <span className="text-xs">(not found)</span>}
                    </button>
                  );
                })}
              </div>

              {/* Quick actions */}
              <div className="flex gap-2 text-sm">
                <button
                  onClick={() => setSlackIds(teamRoster.filter((m) => m.slackId).map((m) => m.slackId!))}
                  className="text-blue-600 hover:underline"
                >
                  Select all
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setSlackIds([])}
                  className="text-blue-600 hover:underline"
                >
                  Clear
                </button>
              </div>

              {/* Show warning if some team members not found */}
              {teamRoster.some((m) => !m.found) && (
                <div className="text-xs text-amber-600">
                  Some team members not found in {env} database. They may need <code>owner_email</code> set.
                </div>
              )}
            </>
          )}

          {/* Selected count */}
          <div className="text-sm text-gray-500">
            {slackIds.length} recipient{slackIds.length !== 1 ? 's' : ''} selected
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 mb-6">
        <Button onClick={() => { void onPreview(); }} loading={loading} disabled={slackIds.length === 0}>
          Preview
        </Button>
        <Button
          variant="primary"
          onClick={() => { void onExecute(); }}
          loading={loading}
          disabled={slackIds.length === 0 || !preview?.valid}
        >
          Execute
        </Button>
      </div>

      {/* Loading State */}
      {loading && loadingMessage && (
        <Card className="mb-4">
          <div className="flex items-center gap-3 py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent" />
            <span className="text-gray-600">{loadingMessage}</span>
          </div>
        </Card>
      )}

      {/* Preview Results */}
      {preview && !loading && (
        <Card className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Preview</h3>
            <div className="flex items-center gap-2">
              {preview.valid ? (
                <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                  <CheckIcon /> Valid
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-red-600 text-sm">
                  <XIcon /> {preview.errors.length} error(s)
                </span>
              )}
              <Button
                variant="ghost"
                className="text-xs"
                onClick={() => setShowRawJson(!showRawJson)}
              >
                {showRawJson ? 'Hide' : 'Show'} Raw JSON
              </Button>
            </div>
          </div>

          {/* Validation Errors */}
          {!preview.valid && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
              <div className="font-medium mb-1">Validation Errors:</div>
              <ul className="list-disc list-inside">
                {preview.errors.map((err, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- validator errors may repeat; composite with index disambiguates
                  <li key={`${err}-${i}`}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-md text-sm">
            <div>
              <span className="text-gray-500">Total Fixtures:</span>
              <div className="font-medium">{preview.summary.totalCount}</div>
            </div>
            <div>
              <span className="text-gray-500">Type:</span>
              <div className="font-medium">{preview.summary.fixtureType}</div>
            </div>
            <div>
              <span className="text-gray-500">UI Type:</span>
              <div className="font-medium font-mono text-xs">{preview.summary.artifactCard}</div>
            </div>
          </div>

          {/* Fixture Cards */}
          <div className="space-y-3">
            {preview.fixtures.slice(0, 5).map((fixture) => (
              <div key={fixture.externalId}>
                <FixtureCard fixture={fixture} />
              </div>
            ))}
            {preview.fixtures.length > 5 && (
              <div className="text-sm text-gray-500 text-center">
                + {preview.fixtures.length - 5} more fixtures
              </div>
            )}
          </div>

          {/* Raw JSON */}
          {showRawJson && (
            <div className="mt-4">
              <pre className="p-3 bg-gray-900 text-gray-100 rounded-md text-xs overflow-auto max-h-96">
                {JSON.stringify(preview, null, 2)}
              </pre>
            </div>
          )}
        </Card>
      )}

      {/* Execution Results */}
      {result && !loading && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Execution Result</h3>
            {result.success ? (
              <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                <CheckIcon /> Success
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-yellow-600 text-sm">
                <WarningIcon /> Partial Success
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-md text-sm">
            <div>
              <span className="text-gray-500">Created:</span>
              <div className="font-medium text-green-600">{result.summary.created}</div>
            </div>
            <div>
              <span className="text-gray-500">Errors:</span>
              <div className="font-medium text-red-600">{result.summary.errors}</div>
            </div>
          </div>

          {result.results.filter((r) => r.status === 'error').length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm">
              <div className="font-medium text-red-800 mb-1">Failed Fixtures:</div>
              {result.results
                .filter((r) => r.status === 'error')
                .map((r) => (
                  <div key={r.externalId} className="text-red-700">
                    {r.externalId}: {r.error}
                  </div>
                ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function formatUnknownValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function FieldSuggestionsBlock({ suggestions }: { suggestions?: FixturePreviewItem['fieldSuggestions'] }) {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div className="mt-2">
      <div className="text-xs text-gray-500 mb-1">Suggested Values:</div>
      <div className="space-y-1">
        {suggestions.map((field, j) => (
          // eslint-disable-next-line react/no-array-index-key -- fieldLabel may repeat across suggestions; composite with index disambiguates
          <div key={`${field.fieldLabel}-${j}`} className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">{field.fieldLabel}:</span>
            <span className="font-medium">{formatUnknownValue(field.proposedValue)}</span>
            {field.confidence != null && (
              <span className="text-xs text-gray-400">
                ({Math.round(field.confidence * 100)}% confidence)
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Fixture Card Component

function FixtureCard({ fixture }: { fixture: FixturePreviewItem }) {
  const [showPayload, setShowPayload] = useState(false);

  return (
    <div className="p-3 border rounded-md">
      {/* Header */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="font-medium">{fixture.artifactName}</div>
          <div className="text-sm text-gray-500">{fixture.summary}</div>
        </div>
        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
          {fixture.recipient.slackId}
        </code>
      </div>

      {/* Field Suggestions */}
      <FieldSuggestionsBlock suggestions={fixture.fieldSuggestions} />

      {/* Ingestion Payload Section */}
      {!!fixture.ingestionPayload && (
        <div className="mt-2 border-t pt-2">
          <button
            type="button"
            onClick={() => setShowPayload(!showPayload)}
            className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
          >
            <ChevronIcon open={showPayload} />
            <span>Ingestion Payload</span>
            <span className="ml-1 text-gray-400">(what will be POSTed)</span>
          </button>

          {showPayload && (
            <div className="mt-2">
              <pre className="text-xs bg-gray-900 text-gray-100 p-2 rounded overflow-auto max-h-64">
                {JSON.stringify(fixture.ingestionPayload, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={clsx('w-3 h-3 transition-transform', open && 'rotate-90')}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

// Icons

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}
